<!-- @format -->
<script setup>
import SmartBox from '../common/SmartBox.vue';
import DestinationSearch from './components/DestinationSearch.vue';
import Occupancy from './components/Occupancy.vue';
import SearchInput from './components/SearchInput.vue';
import BookerCalendar from './components/BookerCalendar.vue';
import { CalendarAvailability } from "../../utils/pricetravel/shared/calendarAvailability";
import BookerTracker from "../../MetricsTracker/booker/metrics";
import { storeToRefs } from 'pinia'
import { useFlightBookerStore } from '../stores/flightBooker'
import { ref, getCurrentInstance, onMounted, computed } from 'vue'

const { proxy } = getCurrentInstance()
const __ = proxy.__
const fn = window.__pt.fn;
const config = window.__pt.settings.site;
const exchange = window.__pt.exchange;
const _tracker = new BookerTracker();
const defaultDate = fn.getDate();
const guestsBox = ref(null)
const calendarBox = ref(null);
const isSwapped = ref(false);
const isMobile = ref(false);
const isModalOpen = ref(false);
const calendarPrices = ref({});
const memoryRoutes = ref([]);

const props = defineProps({
  isList: {
    type: [String, Boolean],
    default: false
  }
});

const store = useFlightBookerStore()
const {
  form,
  fieldErrors,
  book,
  recentSearches,
  popularDestinations,
  searchResults,
  algoliaUrl,
  OpenSearch,
  CloseSearch,
  lastQuery,
  settingPaxes,
  localeDatePicker
} = storeToRefs(store)

const calendarDates = computed({
  get() {
    return {
      start: book.value.checkIn ? new Date(book.value.checkIn) : null,
      end: book.value.checkOut ? new Date(book.value.checkOut) : null
    };
  },
  set(val) {
    book.value.checkIn = val.start ? new Date(val.start) : null;
    book.value.checkOut = val.end ? new Date(val.end) : null;
    updateFormDates(book.value.checkIn, book.value.checkOut);
  }
});

function onSelectDates(val) {
  updateFormDates(val.start, val.end);
  book.value.checkIn = val.start;
  book.value.checkOut = val.end;
  if (calendarBox.value) {
    calendarBox.value.close();
  }
}

let origin = null;
function isValidDate(dateFrom, dateTo) {
  let valid = true;
  dateFrom.setHours(0, 0, 0, 0);
  if (+dateFrom > +dateTo) {
    valid = false;
  }
  return valid;
}

function initDatePicker() {
  let valid = isValidDate(book.value.checkIn, book.value.checkOut);
  if (!valid) {
    book.value.checkOut = store.getInitialDate(book.value.checkIn, config.bookerDefaultCheckOutDay);
  }
  updateFormDates(book.value.checkIn, book.value.checkOut);
}

function updateFormDates(start, end) {
  if (!start) return;
  const format = localeDatePicker.value.format;
  if (form.value.type && end) {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')} - ${dayjs(end).format(format).replace(/\./g, '')}`;
  } else {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')}`;
  }
}

function onOpenSearch(element) {
  setTimeout(() => {
    store.fetchPopularDestinations(element, origin);
  }, 200);
}

async function onSearch(value, mobile, list) {
  if (mobile && list) {
    form.value[list] = value;
  }
  await store.fetchSearchResults(value, mobile, list);
}

function onSelectDestination(item, type, suffix = '') {
  const code = store.selectDestination(item, type, suffix);
  store.setFieldError(type, "");
  if (type === 'from') {
    origin = code;
  }
}

function onChangePaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
}

function onApplyPaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
  // isValidFormApply();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}

function onCancelPaxes() {
  store.updateInputPaxes();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}

function onCloseGuests() {
  store.updateInputPaxes();
}
function isValidFormApply() {
  if (store.validateForm(true)) {
    submitSearch();
  }
}
function submitSearch() {
  store.submitSearch();
}

function ClickChange() {
  isSwapped.value = !isSwapped.value;

  if (book.value.place_name || book.value.place_name_to) {
    _tracker.metricBookerExchangeFlights();
    const tmpPlace = book.value.place_name;
    const tmpSelected = book.value.place_selected;

    book.value.place_name = book.value.place_name_to || "";
    book.value.place_selected = book.value.place_selected_to || {};
    form.value.from = book.value.place_name;

    book.value.place_name_to = tmpPlace || "";
    book.value.place_selected_to = tmpSelected || {};
    form.value.to = book.value.place_name_to;
  }
}
function onChangeFlight() {
  updateFormDates(book.value.checkIn, book.value.checkOut);
}
const hasDataFrom = computed(() => {
  return (searchResults.value?.from?.length || 0) > 0 ||
    (recentSearches.value?.from?.length || 0) > 0 ||
    (popularDestinations.value?.from?.length || 0) > 0;
})

const hasDataTo = computed(() => {
  return (searchResults.value?.to?.length || 0) > 0 ||
    (recentSearches.value?.to?.length || 0) > 0 ||
    (popularDestinations.value?.to?.length || 0) > 0;
})

const shortRoute = computed(() => {
  const ptData = window.__pt?.data || {};
  const from = book.value.place_selected?.code || book.value.place_selected?.airportCode || book.value.place_selected?.cityCode || ptData.startingFromAirport || '';
  const to = book.value.place_selected_to?.code || book.value.place_selected_to?.airportCode || book.value.place_selected_to?.cityCode || ptData.returningFromAirport || '';
  if (from && to) return `${from} - ${to}`;
  return __('booker.destination');
});

const shortMode = computed(() => {
  return form.value.type ? __('booker.round_trip_mobile' + '_' + config.siteName) : __('booker.one_way_mobile' + '_' + config.siteName);
});

const shortPax = computed(() => {
  return store.paxSummary;
});

const closeModal = () => {
  if (props.isList === 'true' || props.isList === true) {
    isModalOpen.value = false;
  }
}

function extractFlightPrices(daysByDate) {
  const prices = {};
  for (const [dateKey, day] of Object.entries(daysByDate || {})) {
    const dateISO = day?.dateISO || day?.date || day?.checkinFormat || dateKey;
    prices[dateISO] = {
      available: day?.Available || day?.available,
      avgPrice: day?.AvgPrice || day?.avgPrice || '',
      color: day?.Color || day?.color || ''
    };
  }
  return prices;
}

async function fetchPriceRangeCalendar(viewDate) {
  if (!config.showRangePriceCalendar) return;
  const origin = book.value.place_selected?.code;
  const destination = book.value.place_selected_to?.code;
  if (!origin || !destination || !viewDate) return;
  const targetMonth = dayjs(viewDate);
  const month = targetMonth.format('MM');
  const year = targetMonth.format('YYYY');

  const routeKey = `flight_${origin}_${destination}_${form.value.type}`;
  if (!memoryRoutes.value.some(m => m == routeKey)) {
      calendarPrices.value = {};
  }

  const response = await CalendarAvailability.getPriceRangeCalendar(
    origin,
    destination,
    year,
    month,
    form.value.type
  );
  const daysByDate = response?.getDataCalendar?.DaysByDate || response?.getDataCalendar?.daysByDate;
  memoryRoutes.value = response?.keys;
  if (daysByDate) {
    calendarPrices.value = {
      ...calendarPrices.value,
      ...extractFlightPrices(daysByDate)
    };
  }
}

onMounted(() => {
  store.initialDefaultDate();
  store.initSearchBox();
  initDatePicker();
  store.loadSearchHistory();
})
</script>
<template>
  <div class="booker-wrapper"
    :class="{ 'is-list-view': isList === 'true' || isList === true, 'is-modal-open': isModalOpen }">
    <!-- Summary Card (Mobile Only) -->
    <button type="button" class="summary-card" ref="summaryBtn" @click="isModalOpen = true"
      :aria-label="__('booker.modify_search')">
      <span class="summary-card__mode font-12 font-500 font-main">{{ shortMode }}</span>
      <div class="summary-card__wrapper">
        <span class="summary-card__box">
          <span class="summary-card__data">
            <span class="summary-card__top">
              <span class="summary-card__dest font-14 font-500 font-main">{{ shortRoute }}</span>
            </span>
            <span class="summary-card__info font-14 font-400 font-main text-disabled">
              {{ form.dates }} | {{ shortPax }}
            </span>
          </span>
        </span>
        <span class="summary-card__btn" aria-hidden="true">
          <span class="icon font-icons icons-search font-24"></span>
        </span>
      </div>
    </button>

    <!-- Form Container -->
    <div class="booker-form-container">
      <div class="booker-modal__overlay" @click="isModalOpen = false" aria-hidden="true"></div>
      <div class="booker-modal__content" :role="isModalOpen ? 'dialog' : null"
        :aria-modal="isModalOpen ? 'true' : null" aria-labelledby="booker-modal-title"
        @keydown.esc="closeModal">

        <header class="booker-modal__header">
          <h2 id="booker-modal-title" class="font-16 font-500 font-main m-0">{{ __('booker.modify_search') }}</h2>
          <button type="button" class="booker-modal__close" ref="modalCloseBtn" @click="isModalOpen = false"
            :aria-label="__('messages.close')">
            <span class="icon font-icons icons-close font-24" aria-hidden="true"></span>
          </button>
        </header>

        <form class="search" @submit.prevent="submitSearch" :aria-label="__('booker.flights')">
    <div class="search__filters">
      <div class="group">
        <legend class="group__legend font-14 font-400 font-strong">
          {{ __('booker.type') }}
        </legend>
        <ul class="group__list">
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="flightType" :value="false"
                v-on:change="onChangeFlight" v-model="form.type" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">{{ __('booker.one_way' + '_' +
                config.siteName) }}</span>
            </label>
          </li>
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="flightType" :value="true"
                v-on:change="onChangeFlight" v-model="form.type" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">{{ __('booker.round_trip' + '_' +
                config.siteName) }}</span>
            </label>
          </li>
        </ul>
      </div>
    </div>
    <div class="search__inputs">
      <div class="search__route">
        <SmartBox @open="onOpenSearch('from')" :title="__('booker.origin_sub')" :OpenBox="OpenSearch.from"
          :CloseBox="CloseSearch.from" :isHeaderActive="false" :hasContent="hasDataFrom" @isMobile="isMobile = $event">
          <template #trigger>
            <SearchInput id="origin" :label="__('booker.origin')" :placeholder="__('booker.origin')" v-model="form.from"
              :selectOnFocus="!isMobile" :activeFocus="!isMobile" @input="onSearch($event, false, 'from')"
              :debounce="700" :autocomplete="'off'" :spellcheck="false" :error="!!fieldErrors.from"
              :errorMessage="fieldErrors.from" :readonly="isMobile">
              <template #icon>
                <span class="search__icon font-20 icons-plane-departure"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <DestinationSearch :product="'F'" :recentSearches="recentSearches.from" :origin="'from'"
              :popularDestinations="popularDestinations.from" @update:query="onSearch($event, true, 'from')"
              :typeBooker="'from'" :destination="form.from" :imgUrl="algoliaUrl" :searchResults="searchResults.from"
              @update:selectedDestination="onSelectDestination($event, 'from', '')" :lastQuery="lastQuery"
              :label="__('booker.origin')" :placeholder="''" :icon="'icons-plane-departure'">
            </DestinationSearch>
          </template>
        </SmartBox>
        <button type="button" class="search__swap" :class="{ 'is-swapped': isSwapped }"
          aria-label="Intercambiar origen y destino" @click="ClickChange">
          <span class="search__swap-icon font-24 font-md-20 icons-arrow-right-arrow-left-regular-full"></span>
        </button>
        <SmartBox @open="onOpenSearch('to')" :title="__('booker.destination_sub')" :OpenBox="OpenSearch.to"
          :CloseBox="CloseSearch.to" :isHeaderActive="false" :hasContent="hasDataTo" @isMobile="isMobile = $event">
          <template #trigger>
            <SearchInput id="destination" :label="__('booker.destination')" :placeholder="__('booker.destination')"
              :selectOnFocus="!isMobile" :activeFocus="!isMobile" v-model="form.to"
              @input="onSearch($event, false, 'to')" :debounce="700" :autocomplete="'off'" :spellcheck="false"
              :error="!!fieldErrors.to" :errorMessage="fieldErrors.to" :readonly="isMobile">
              <template #icon>
                <span class="search__icon font-20 icons-plane-arrival"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <DestinationSearch :product="'F'" :recentSearches="recentSearches.to"
              :popularDestinations="popularDestinations.to" @update:query="onSearch($event, true, 'to')"
              :destination="form.to" :imgUrl="algoliaUrl" :searchResults="searchResults.to"
              @update:selectedDestination="onSelectDestination($event, 'to', '_to')" :lastQuery="lastQuery"
              :typeBooker="'to'" :label="__('booker.destination')" :placeholder="''" :icon="'icons-plane-arrival'">
            </DestinationSearch>
          </template>
        </SmartBox>
      </div>
      <div class="search__details">
        <SmartBox ref="calendarBox" :title="form.type ? __('booker.dates') : __('booker.date')" :isHeaderActive="false"
          :mobile-breakpoint="768">
          <template #trigger>
            <SearchInput id="calendar-checkIn-flight" type="text" required name="calendar-checkIn-flight"
              :activeFocus="true" :label="form.type ? __('booker.dates') : __('booker.date')" :readonly="true"
              v-model="form.dates" :error="!!fieldErrors.dates" :errorMessage="fieldErrors.dates">
              <template #icon>
                <span class="icon font-icons icons-calendar-depart font-20"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <BookerCalendar :modelValue="calendarDates" :currency="exchange.currency"
              :mode="form.type ? 'range' : 'single'" :unit="'days'" :minDays="1" :minDate="defaultDate"
              :startLabel="__('booker.departure')" :endLabel="__('booker.return')" :prices="calendarPrices" :isFlight="true"
              @select="onSelectDates" @close="calendarBox.close()" @viewDate="fetchPriceRangeCalendar" />
          </template>
        </SmartBox>
        <SmartBox ref="guestsBox" :title="__('booker.passengers_sub')" :isHeaderActive="false" @close="onCloseGuests"
          :mobile-breakpoint="768">
          <template #trigger>
            <SearchInput id="guests" :label="__('booker.passengers')" :placeholder="form.guests" :readonly="true"
              :activeFocus="true" v-model="form.guests" :error="!!fieldErrors.guests"
              :errorMessage="fieldErrors.guests">
              <template #icon>
                <span class="icon font-icons icons-guests font-20"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <Occupancy :product="'F'" :settingPaxes="settingPaxes" :counts="book.paxes" @change="onChangePaxes"
              @apply="onApplyPaxes" @cancel="onCancelPaxes">
            </Occupancy>
          </template>
        </SmartBox>
      </div>
    </div>
    <div class="search__action search__action--mobile">
      <button type="submit" class="search__btn btnPrimary btnPrimary--md">
        {{ __('booker.button') }}
      </button>
    </div>
  </form>
  </div>
  </div>
  </div>
</template>

<style scoped lang="scss">
.search {
  background-color: var(--bg-info-strong);
  padding: var(--space-16);
  border-radius: var(--space-12);
  display: flex;
  flex-direction: column;
  background-color: var(--bg-base);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  gap: var(--space-12);
  box-shadow: var(--shadow-200);

  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: auto;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'flights flights'
      'flights flights'
      'filters action';
    gap: var(--space-20);
    width: 100%;
    padding-inline: 0px;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    box-shadow: none;
    border: 0px;
    margin: 0px auto;
  }

  /* DESKTOP LAYOUT (> 1280px) */
  @media (min-width: 1280px) {
    grid-template-areas:
      'flights flights action'
      'filters filters filters';
    gap: var(--space-12);
    padding: var(--space-20);
    width: 100%;
    border-radius: var(--space-12);
    border: 1px solid var(--border-subtle);
  }

  &__icon {
    display: flex;
  }

  &__inputs {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    /* TABLET (> 768px) */
    @media (min-width: 768px) {
      grid-area: flights;
      gap: var(--space-20);
    }

    /* DESKTOP (> 1280px) */
    @media (min-width: 1280px) {
      display: grid;
      grid-template-columns: minmax(50%, 556px) minmax(378px, 1fr);
      align-items: center;
      gap: var(--space-12);
    }
  }

  &__route {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);
    position: relative;

    @media (min-width: 768px) {
      flex-direction: row;
      gap: var(--space-4);
      width: 100%;
      align-items: center;
    }

    @media (min-width: 1280px) {
      flex: 1;
    }
  }

  /* Botón Swap */
  &__swap {
    position: absolute;
    right: 24px;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    width: 36px;
    height: 36px;
    border-radius: var(--space-4);
    border: 1px solid var(--border-subtle);
    color: var(--bg-info-strong);
    background: var(--bg-base);
    transform: translateY(-50%);
    cursor: pointer;

    @media (min-width: 768px) {
      position: unset;
      min-width: 24px;
      height: 24px;
      transform: none;
    }

    &:hover {
      background-color: var(--bg-primary-subtle);
      color: var(--text-primary);
      border: 1px solid var(--border-primary);
    }

    &-icon {
      display: flex;
      transform: rotate(90deg);
      transition: transform 0.4s ease-in-out;

      @media (min-width: 768px) {
        transform: rotate(0deg);
      }
    }

    &.is-swapped &-icon {
      transform: rotate(270deg);

      @media (min-width: 768px) {
        transform: rotate(180deg);
      }
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    @media (min-width: 768px) {
      flex-direction: row;
      gap: var(--space-32);

      &>* {
        width: 50%;
      }
    }

    @media (min-width: 1280px) {
      flex: 1;
      gap: var(--space-12);
    }
  }

  &__date,
  &__guests {
    @media (min-width: 1280px) {
      flex: 1;
    }
  }

  &__action {
    @media (min-width: 768px) {
      display: flex;
      justify-content: end;
    }

    @media (min-width: 1280px) {
      grid-area: action;
      width: fit-content;
    }
  }

  &__btn {
    width: 100%;

    @media (min-width: 768px) {
      width: 160px;
    }

    @media (min-width: 1280px) {
      width: 114px;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
  }
}

.group {
  border: none;
  padding: 0px;
  margin: 0px;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: var(--space-12);
  }

  &__list {
    display: flex;
    gap: var(--space-16);
    list-style: none;
    padding: 0px;
    margin: 0px;
  }

  &__legend {
    display: none;
    width: fit-content;
    margin: 0px;

    @media (min-width: 768px) {
      display: flex;
    }
  }
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  cursor: pointer;
  margin: 0px;

  .input--radio {
    cursor: pointer;
  }
}

.booker-wrapper.is-list-view {
  @media (max-width: 767px) {
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
    background: var(--bg-base);
    border-bottom: none;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 10;

    .summary-card {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: var(--space-4);
      cursor: pointer;
      width: 100%;
      background: none;
      border: none;
      padding: var(--space-8) var(--space-16);
      margin: 0;
      text-align: left;
      font: inherit;
      color: inherit;

      &__wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-16);
        width: 100%;
      }

      &__box {
        flex: 1;
        display: flex;
        align-items: flex-start;
        gap: var(--space-8);
        padding: var(--space-4) var(--space-8);
        border: 1px solid var(--border-subtle);
        border-radius: var(--space-8);
        background: var(--bg-base);
        overflow: hidden;
      }

      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin-top: 4px;

        .icon {
          display: flex;
        }
      }

      &__data {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
      }

      &__top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
      }

      &__dest {
        color: var(--text-main);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__mode {
        color: var(--text-main);
        white-space: nowrap;
        margin-left: 0;
      }

      &__info {
        color: var(--text-muted);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__btn {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        color: var(--bg-primary);
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
      }
    }

    &.is-modal-open .summary-card {
      display: none;
    }

    .booker-form-container {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 0.35s cubic-bezier(0.22, 1, 0.36, 1);

      .booker-modal__overlay {
        display: none;
      }

      .booker-modal__content {
        overflow: hidden;
        min-height: 0;
        background-color: var(--bg-base);
        width: 100%;
        display: flex;
        flex-direction: column;
        opacity: 0;
        transition: opacity 0.12s ease;
      }

      .booker-modal__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-16) var(--space-16) 0px var(--space-16);

        h2 {
          font-family: var(--basefontFamily);
        }
      }

      .booker-modal__close {
        background: transparent;
        border: none;
        cursor: pointer;
        display: flex;
        color: var(--text-main);

        &:focus-visible {
          outline: 2px solid var(--border-focus, var(--bg-primary));
          outline-offset: 2px;
          border-radius: var(--space-4, 4px);
        }
      }

      .search {
        display: flex;
        flex-direction: column;
        box-shadow: none;
        border: none;
        margin: 0;
        width: 100%;

        .search__inputs {
          display: flex;
          flex-direction: column;
          gap: var(--space-16, 16px);
        }

        .search__action {
          display: flex;
          justify-content: stretch;

          .search__btn {
            width: 100%;
          }
        }
      }
    }

    &.is-modal-open .booker-form-container {
      grid-template-rows: 1fr;

      .booker-modal__content {
        opacity: 1;
        transition: opacity 0.2s ease 0.14s;
      }
    }

    @media (prefers-reduced-motion: reduce) {

      .booker-form-container,
      .booker-form-container .booker-modal__content {
        transition: none;
      }
    }
  }

  @media (min-width: 768px) {
    margin: 0;
    width: auto;
    position: static;
    transform: none;

    .summary-card {
      display: none;
    }

    .booker-form-container {
      display: block;
    }

    .booker-modal__overlay,
    .booker-modal__header {
      display: none;
    }
  }
}

.booker-wrapper:not(.is-list-view) {

  .summary-card,
  .booker-modal__overlay,
  .booker-modal__header {
    display: none;
  }
}
</style>
